import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import Warehouses from './pages/Warehouses';
import Articles from './pages/Articles';
import Movements from './pages/Movements';
import WarehouseTransfers from './pages/WarehouseTransfers';
import Users from './pages/Users';
import Reports from './pages/Reports';
import Profile from './pages/Profile';
import PurchaseRequests from './pages/PurchaseRequests';
import PurchaseOrders from './pages/PurchaseOrders';
import Receptions from './pages/Receptions';
import OtrasCompras from './pages/OtrasCompras';
import SupplierAccounts from './pages/SupplierAccounts';
import ArcaConfig from './pages/ArcaConfig';
import PurchaseFiscalDocuments from './pages/PurchaseFiscalDocuments';
import Audit from './pages/Audit';
import Traceability from './pages/Traceability';
import Suppliers from './pages/Suppliers';
import SupplierMerge from './pages/SupplierMerge';
import InternalAreas from './pages/InternalAreas';
import InternalRequests from './pages/InternalRequests';
import InternalReturns from './pages/InternalReturns';
import InternalConsumptions from './pages/InternalConsumptions';
import AreaTransfers from './pages/AreaTransfers';
import Maintenance from './pages/Maintenance';
import MaintenanceCenter from './pages/MaintenanceCenter';
import Residents from './pages/Residents';
import ResidentsReports from './pages/ResidentsReports';
import Services from './pages/Services';
import Billing from './pages/Billing';
import BatchCreditNote from './pages/BatchCreditNote';
import HealthInsurers from './pages/HealthInsurers';
import WarehouseStock from './pages/WarehouseStock';
import StockCounts from './pages/StockCounts';
import Treasury from './pages/Treasury';
import TreasuryConfig from './pages/TreasuryConfig';
import TreasuryControl from './pages/TreasuryControl';
import TreasuryOperations from './pages/TreasuryOperations';
import TreasuryReports from './pages/TreasuryReports';
import ChartOfAccounts from './pages/ChartOfAccounts';
import AccountingJournal from './pages/AccountingJournal';
import AccountingLedger from './pages/AccountingLedger';
import AccountingTrialBalance from './pages/AccountingTrialBalance';
import AccountingSalesIvaBook from './pages/AccountingSalesIvaBook';
import AccountingPurchaseIvaBook from './pages/AccountingPurchaseIvaBook';
import AccountingClosures from './pages/AccountingClosures';
import AccountingIvaPosition from './pages/AccountingIvaPosition';
import Debtors from './pages/Debtors';
import UnmetDemand from './pages/UnmetDemand';
import NursingReceptions from './pages/NursingReceptions';
import NursingStock from './pages/NursingStock';
import Help from './pages/Help';

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id in URL fragment during render (NOT in useEffect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/login" element={<Login />} />
        
        <Route path="/" element={<ProtectedRoute />}>
          <Route index element={<Dashboard />} />
          <Route path="warehouses" element={<Warehouses />} />
          <Route path="warehouses/:id/stock" element={<WarehouseStock />} />
          <Route path="articles" element={<Articles />} />
          <Route path="movements" element={<Movements />} />
          <Route path="warehouse-transfers" element={<WarehouseTransfers />} />
          <Route path="stock-counts" element={<StockCounts />} />
          <Route path="area-stock-counts" element={<StockCounts scope="area" />} />
          <Route path="purchase-requests" element={<PurchaseRequests />} />
          <Route path="purchases/unmet-demand" element={<UnmetDemand />} />
          <Route path="nursing-receptions" element={<NursingReceptions />} />
          <Route path="nursing-stock" element={<NursingStock />} />
          <Route path="purchase-orders" element={<PurchaseOrders />} />
          <Route path="receptions" element={<Receptions />} />
          <Route path="other-purchases" element={<OtrasCompras />} />
          <Route path="supplier-accounts" element={<SupplierAccounts />} />
          <Route path="arca-config" element={<ArcaConfig />} />
          <Route path="purchases/fiscal-documents" element={<PurchaseFiscalDocuments />} />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="suppliers/merge" element={<SupplierMerge />} />
          <Route path="internal-areas" element={<InternalAreas />} />
          <Route path="internal-requests" element={<InternalRequests />} />
          <Route path="internal-returns" element={<InternalReturns />} />
          <Route path="internal-consumptions" element={<InternalConsumptions />} />
          <Route path="area-transfers" element={<AreaTransfers />} />
          <Route path="users" element={<Users />} />
          <Route path="reports" element={<Reports />} />
          <Route path="traceability" element={<Traceability />} />
          <Route path="audit" element={<Audit />} />
          <Route path="maintenance" element={<Maintenance />} />
          <Route path="maintenance-center" element={<MaintenanceCenter />} />
          <Route path="residents" element={<Residents />} />
          <Route path="residents/reports" element={<ResidentsReports />} />
          <Route path="resident-services" element={<Services />} />
          <Route path="billing" element={<Billing />} />
          <Route path="billing/batch-credit-note" element={<BatchCreditNote />} />
          <Route path="health-insurers" element={<HealthInsurers />} />
          <Route path="treasury" element={<Treasury />} />
          <Route path="treasury/config" element={<TreasuryConfig />} />
          <Route path="treasury/operations" element={<TreasuryOperations />} />
          <Route path="treasury/control" element={<TreasuryControl />} />
          <Route path="treasury/reports" element={<TreasuryReports />} />
          <Route path="treasury/payroll-search" element={<Navigate to="/treasury/reports?tab=payroll" replace />} />
          <Route path="accounting/chart-of-accounts" element={<ChartOfAccounts />} />
          <Route path="accounting/journal" element={<AccountingJournal />} />
          <Route path="accounting/ledger" element={<AccountingLedger />} />
          <Route path="accounting/trial-balance" element={<AccountingTrialBalance />} />
          <Route path="accounting/iva-sales-book" element={<AccountingSalesIvaBook />} />
          <Route path="accounting/iva-purchase-book" element={<AccountingPurchaseIvaBook />} />
          <Route path="accounting/closures" element={<AccountingClosures />} />
          <Route path="accounting/iva-purchase-closures" element={<AccountingClosures />} />
          <Route path="accounting/iva-position" element={<AccountingIvaPosition />} />
          <Route path="billing/debtors" element={<Debtors />} />
          <Route path="profile" element={<Profile />} />
          <Route path="help/:doc" element={<Help />} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
}

export default AppRouter;